import { useInfiniteQuery } from "@tanstack/react-query";
import {
  getShopifyProductId,
  getShopifyShopId,
  isOrderByOrdersCountRequested,
} from "@/utils/metadata";
import { getVisitorId } from "@/utils/tracking";

const API_BASE_URL =
  import.meta.env.VITE_API_URL || "https://api.socialocean.co/api";

const PER_PAGE = 10;

export interface Review {
  /** Server id — the like-toggle key and the unit the heart state hangs off. */
  id?: number;
  name: string;
  rating: number;
  comment: string;
  created_at: string;
  import_source?: string | null;
  /** First order date (fallback: signup date) — "Customer since X". */
  customer_since?: string | null;
  orders_count?: number | null;
  /** Verbatim quote matching the active topic filter; highlighted in the text. */
  highlight_quote?: string | null;
  /** Total likes for the review (visitor-agnostic). */
  likes_count?: number;
  /** Whether the current visitor has liked it — only set when visitorId is sent. */
  liked?: boolean;
}

export interface ReviewFilter {
  key: string;
  label: string;
  emoji: string;
  count: number;
}

interface ReviewsPage {
  reviews: Review[];
  total: number; // favorites count (label) — identical on every page; we read page 0
  product_id: number | null;
  store_domain: string | null;
  filters: ReviewFilter[]; // curated chips, identical on every page; read page 0
  reviewsTotal: number; // pagination.total — count of reviews in the (filtered) set
  nextPage: number | null; // from data.pagination.next_page
}

export interface ReviewsData {
  reviews: Review[]; // flattened across all loaded pages
  total: number; // favorites count
  product_id: number | null;
  store_domain: string | null;
  filters: ReviewFilter[];
  reviewsTotal: number;
}

const EMPTY_PAGE: ReviewsPage = {
  reviews: [],
  total: 0,
  product_id: null,
  store_domain: null,
  filters: [],
  reviewsTotal: 0,
  nextPage: null,
};

/**
 * @param productIdOverride Load this product's reviews instead of the page's
 *   own -- set by the product tabs (see getProductTabs). Omit for the page.
 */
export function useReviews(
  topic: string | null = null,
  productIdOverride: number | null = null,
) {
  const productId = productIdOverride ?? getShopifyProductId();
  const shopId = getShopifyShopId();
  // Opt-in via `?orders_count=true` on the storefront page URL; forwarded so the
  // backend sorts reviews by reviewer order count (desc). Part of the query key
  // so a different value caches as a distinct list.
  const orderByOrdersCount = isOrderByOrdersCountRequested();

  const query = useInfiniteQuery({
    queryKey: ["reviews", productId, shopId, PER_PAGE, topic, orderByOrdersCount],
    initialPageParam: 1,
    enabled: !!productId && !!shopId,
    placeholderData: (previousData) => previousData,
    getNextPageParam: (lastPage: ReviewsPage) => lastPage.nextPage ?? undefined,
    queryFn: async ({ pageParam }): Promise<ReviewsPage> => {
      if (!productId || !shopId) return EMPTY_PAGE;

      const url = new URL(`${API_BASE_URL}/widget/reviews/${productId}`);
      url.searchParams.set("shop", shopId);
      url.searchParams.set("page", String(pageParam));
      url.searchParams.set("per_page", String(PER_PAGE));
      if (topic) url.searchParams.set("topic", topic);
      if (orderByOrdersCount) url.searchParams.set("orders_count", "true");
      // Sent so each review comes back with the visitor's `liked` state; the
      // count is accurate either way, `liked` is just false without it.
      const visitorId = getVisitorId();
      if (visitorId) url.searchParams.set("visitorId", visitorId);

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
        },
        credentials: "omit",
      });

      // Must throw, not resolve. Resolving an empty page here made a transient
      // blip look like a legitimate last page: React Query cached it with
      // `nextPage: null`, `hasNextPage` went false, the infinite-scroll sentinel
      // unmounted, and the list sat on page 1 for good — no retry, because
      // nothing had failed as far as the query was concerned.
      if (!response.ok) {
        throw new Error(`Reviews request failed: ${response.status}`);
      }

      const json = await response.json();
      const d = json.data ?? {};

      return {
        reviews: d.reviews ?? [],
        total: d.total ?? 0,
        product_id: d.product_id ?? null,
        store_domain: d.store_domain ?? null,
        filters: d.filters ?? [],
        reviewsTotal: d.pagination?.total ?? 0,
        nextPage: d.pagination?.next_page ?? null,
      };
    },
  });

  const pages = query.data?.pages ?? [];
  const firstPage = pages[0];

  // Backward-compatible shape: `reviews` flattens every loaded page, while
  // `total`/`product_id`/`store_domain`/`filters`/`reviewsTotal` come from
  // page 0 so they stay stable as more pages load (total = favorites, drives
  // the ratings label + DOM sync; reviewsTotal = size of the filtered set).
  const data: ReviewsData | undefined = query.data
    ? {
        reviews: pages.flatMap((p) => p.reviews),
        total: firstPage?.total ?? 0,
        product_id: firstPage?.product_id ?? null,
        store_domain: firstPage?.store_domain ?? null,
        filters: firstPage?.filters ?? [],
        reviewsTotal: firstPage?.reviewsTotal ?? 0,
      }
    : undefined;

  return {
    data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    // True while `data` is still the PREVIOUS topic's list during a filter
    // switch — consumers show skeletons instead of the wrong reviews.
    isPlaceholderData: query.isPlaceholderData,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    /**
     * The last attempt at a further page failed. Consumers use this to stop
     * auto-fetching — the sentinel would otherwise re-fire the moment the
     * spinner clears and hammer a failing endpoint — and to offer a manual
     * retry instead.
     */
    isFetchNextPageError: query.isFetchNextPageError,
  };
}
